// CRM TypeScript Interfaces - Event-Sourced CRM

// ====================
// Event-Sourced CRM Types
// ====================

/**
 * System entity type names (ahoy_* types) — the single runtime source of truth.
 *
 * Both the `EntityTypeName` compile-time type below and the runtime
 * `isValidEntityTypeName` allowlist (lib/crm/core/types.ts) derive from this
 * array, so the type and the validator can never drift. Add new system types
 * here only.
 */
export const SYSTEM_ENTITY_TYPE_NAMES = [
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_note',
  'ahoy_task',
  'ahoy_meeting',
  'ahoy_booking_type',
  'ahoy_booking',
  'ahoy_enrichment_request',
  'ahoy_external_integration',
  'ahoy_user',
  'ahoy_org',
  'ahoy_email_thread',
  'ahoy_property_type',
  'ahoy_import',
  'ahoy_research',
  'ahoy_risk',
  'ahoy_deal_pipeline',
  'ahoy_deal_pipeline_stage',
  'ahoy_contact_lifecycle_stage',
  'ahoy_meeting_notes',
  'ahoy_file',
  'ahoy_event_automation',
  'ahoy_scheduled_automation',
  'ahoy_automation_webhook',
  'ahoy_outbound_webhook',
  'ahoy_audit',
  'ahoy_team',
  'ahoy_web_form',
  'ahoy_reminder',
  'ahoy_validation_rule',
  'ahoy_service_account',
  'ahoy_agent',
  'ahoy_secret',
  'ahoy_stripe_subscription',
] as const;

/**
 * Entity type names supported by the system.
 * Includes system types and custom org-specific types (org_*).
 */
export type EntityTypeName =
  | (typeof SYSTEM_ENTITY_TYPE_NAMES)[number]
  | `org_${string}`;

/**
 * Property value with metadata from typed property tables.
 * Contains the actual value plus optional source tracking information.
 * Metadata fields are optional since frontend only needs the value for display.
 */
export interface PropertyValue {
  value: unknown;
  source_type_name?: string;
  source_id?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * CRM Object - Read-only projection of an event stream.
 * This is what you get when querying objects.
 *
 * Note: As of property-defaults feature:
 * - pipeline_name/stage_name removed - use associations for stages
 * - Use properties field for typed property values (with metadata)
 */
export type AccessRuleKind =
  | 'EVERYONE_AT_ORG'
  | 'MEETING_ATTENDEES'
  | 'THREAD_RECIPIENTS'
  | 'FILE_UPLOADER_ONLY'
  | 'FILE_INHERITED_FROM_PARENT'
  | 'INHERITED_PARENT_HIDDEN';

export interface AccessRule {
  kind: AccessRuleKind;
  parent_entity_type: string | null;
  parent_entity_id: string | null;
}

export interface SettingsLink {
  url: string;
  provider_label: string;
}

export interface AccessSummary {
  privacy_state: 'org' | 'private';
  access_rules: AccessRule[];
  settings_link: SettingsLink | null;
  viewer_is_owner: boolean;
}

export interface CrmObject {
  id: string; // UUID - entity identifier (canonical ID)
  org_id: number; // Org ID
  entity_id: string; // UUID - returns the same value as id
  entity_type_name: EntityTypeName; // Entity type - canonical identifier for object type
  properties?: Record<string, PropertyValue>; // Typed property values with metadata
  last_event_seq: number; // Last event sequence number for optimistic concurrency
  associations?: AssociationDetail[]; // Associations from this object
  associated_objects?: Record<string, CrmObject>; // hydrated lookup map; populated when ?include_associated_objects=true, {} otherwise
  deleted_at: string | null;
  access_summary?: AccessSummary | null;
}

/**
 * Association between CRM objects (legacy format).
 */
export interface Association {
  id: number;
  association_type_name: string; // e.g., "ahoy_contact_to_company"
  to_entity_id: string;
  to_entity_type_name: string;
  created_at: string;
}

/**
 * Association detail from event-sourced CRM.
 */
export interface AssociationDetail {
  association_type_name: string;
  target_entity_id: string;
}

/**
 * Event types supported by the event-sourced CRM.
 */
export type EventType =
  | 'ahoy_object_created'
  | 'ahoy_property_updated'
  | 'ahoy_association_added'
  | 'ahoy_association_removed'
  | 'ahoy_object_deleted'
  | 'ahoy_object_merged'
  | 'ahoy_note_added'
  | 'ahoy_email_sent'
  | 'ahoy_email_received';

/**
 * Event for submission to the event-sourced CRM.
 * All events must include the 9 required fields per API validation.
 */
export interface CrmEvent {
  // Required fields for ALL events
  entity_id: string; // UUID - entity identifier
  entity_type_name: string; // e.g., "ahoy_contact", "ahoy_company", "ahoy_deal"
  event_type_name: string; // Event type (use EventType)
  seq: number; // Sequence number (usually 1 for frontend)
  idempotency_key: string; // UUID for idempotency
  occurred_at: string; // ISO 8601 timestamp
  payload: Record<string, unknown>; // Event-specific data
  source_type_name: string; // e.g., "web-app"
  source_id: string; // e.g., "frontend"
}

/**
 * Per-conflict acknowledgement for the overwrite path.
 * Pairs an entity_id with the association_type_name the user authorized
 * overwriting on — scoping authorization to the (entity_id, association_type)
 * pair so acknowledging one conflict cannot cause unintended removals on a
 * different association type targeting the same entity.
 */
export interface OverwriteTarget {
  entity_id: string;
  association_type_name: string;
}

/**
 * Batch of events to submit.
 */
export interface EventBatch {
  events: CrmEvent[];
  /**
   * Per-conflict authorizations to overwrite existing single-cardinality
   * associations. Each entry authorizes a removal scoped to one
   * (entity_id, association_type_name) pair.
   */
  overwrite_targets?: OverwriteTarget[];
}

/**
 * Single event result from batch submission.
 * Contains the post-processed event data (after server-side rewrites).
 */
export interface EventResult {
  event_id: number;
  entity_id: string;
  idempotency_key: string;
  recorded_at: string;
  occurred_at: string; // ISO 8601 timestamp when the event occurred (vs recorded_at which is DB write time)
  seq: number;
  event_type_name: string;
  entity_type_name: string;
  payload: Record<string, unknown>;
  seq_resolved?: boolean;
  was_deduplicated?: boolean; // Present and true when this result represents an event matched by idempotency key
}

/**
 * Response from event submission.
 */
export interface EventBatchResponse {
  results: EventResult[];
  events_processed: number;
  success: boolean;
}

/** 202 response when a single-object delete is dispatched to a background job. */
export interface AsyncSingleObjectDeleteResponse {
  success: true;
  mode: 'async';
  job_id: number;
  total_count: number;
  status_url: string;
}

export type SubmitEventsResponse =
  | EventBatchResponse
  | AsyncSingleObjectDeleteResponse;

export function isAsyncSingleObjectDeleteResponse(
  r: SubmitEventsResponse
): r is AsyncSingleObjectDeleteResponse {
  return (r as AsyncSingleObjectDeleteResponse).mode === 'async';
}

/**
 * Property filter operators.
 */
export type PropertyFilterOperator =
  | 'exact'
  | 'ne'
  | 'isnull'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'startswith'
  | 'endswith'
  | 'contains'
  | 'not_contains'
  | 'contains_any'
  | 'not_contains_any'
  | 'in'
  | 'notin';

/**
 * Quantity value type for currency amounts, weights, etc.
 * Used in filters and property values.
 */
export interface QuantityValue {
  amount: number | string;
  unit: string;
}

/**
 * All possible filter value types.
 * Includes primitives, arrays, and structured types.
 */
export type FilterValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | QuantityValue
  | string[]
  | QuantityValue[];

/**
 * Single property filter for query endpoint.
 */
export interface PropertyFilter {
  property: string;
  operator: PropertyFilterOperator;
  value:
    | string
    | number
    | boolean
    | null
    | string[]
    | number[]
    | QuantityValue
    | QuantityValue[];
}

/**
 * Association filter operators.
 * These are a subset of property filter operators that make sense for associations.
 */
export type AssociationFilterOperator =
  | 'exact'
  | 'ne'
  | 'in'
  | 'notin'
  | 'isnull'
  // Matches rows whose association of this type points to ANY of the provided
  // entity_ids OR has no association of this type at all. Equivalent to
  // `(in value) OR (isnull true)` collapsed into a single operator so callers
  // don't need to double their OR-groups. Used by the Calendar page.
  | 'in_or_isnull';

/**
 * Association filter for filtering by associated entity(s).
 * Mutually exclusive with PropertyFilter - a filter is either property-based or association-based.
 */
export interface AssociationFilter {
  association: string; // Association type name (e.g., "ahoy_contact_to_company")
  operator: AssociationFilterOperator;
  value: string | string[] | boolean | null; // entity_id(s) or boolean for isnull
}

/**
 * Associated-property filter: match source rows by a PROPERTY of an associated
 * target record (single association hop, EXISTS semantics). Distinguished from
 * AssociationFilter (which matches by associated record id) by the presence of
 * `target_property`. The value/operator semantics are those of the target
 * property's field type — not entity ids — so the value type mirrors
 * PropertyFilter.
 *
 * Example: "Contacts where the associated Company's Industry is Technology" →
 * { association: 'ahoy_contact_to_company', target_property: 'ahoy_industry',
 *   operator: 'exact', value: 'Technology' }
 */
export interface AssociatedPropertyFilter {
  association: string; // Association type name (e.g., "ahoy_contact_to_company")
  target_property: string; // Property on the association's target entity type
  operator: PropertyFilterOperator;
  value: PropertyFilter['value'];
}

/**
 * Entity ID filter: match records whose entity_id (UUID primary key) is in or
 * not in a provided list. Distinguished from PropertyFilter by the `subject` key.
 */
export interface EntityIdFilter {
  subject: 'entity_id';
  operator: 'in' | 'notin';
  value: string[];
}

/**
 * Nested OR-block: matches rows where ANY of the child filters match.
 * Carries no operator/value of its own — only a non-empty array of child
 * filters, which may themselves be nested any_of/all_of blocks.
 */
export interface AnyOfFilter {
  any_of: CrmFilter[];
}

/**
 * Nested AND-block: matches rows where ALL of the child filters match.
 * Carries no operator/value of its own — only a non-empty array of child
 * filters, which may themselves be nested any_of/all_of blocks.
 */
export interface AllOfFilter {
  all_of: CrmFilter[];
}

/**
 * Union type for filters - property-based, association-based (by id),
 * associated-property-based (by a target record's property), entity-id-based,
 * or a nested any_of/all_of block. These are mutually exclusive: a filter has
 * exactly one of `property`, `association` (alone), `association` +
 * `target_property`, `subject`, `any_of`, or `all_of`.
 */
export type CrmFilter =
  | PropertyFilter
  | AssociationFilter
  | AssociatedPropertyFilter
  | EntityIdFilter
  | AnyOfFilter
  | AllOfFilter;

/**
 * Type guard to check if a filter is an EntityIdFilter.
 */
export function isEntityIdFilter(filter: CrmFilter): filter is EntityIdFilter {
  return 'subject' in filter && filter.subject === 'entity_id';
}

/**
 * Type guard to check if a filter is a PropertyFilter.
 */
export function isPropertyFilter(filter: CrmFilter): filter is PropertyFilter {
  return 'property' in filter;
}

/**
 * Type guard to check if a filter is an AssociatedPropertyFilter (filters by a
 * property of an associated record). Must be checked BEFORE isAssociationFilter
 * since an associated-property filter also carries an `association` key.
 */
export function isAssociatedPropertyFilter(
  filter: CrmFilter
): filter is AssociatedPropertyFilter {
  return 'association' in filter && 'target_property' in filter;
}

/**
 * Type guard to check if a filter is an AssociationFilter (matches by associated
 * record id). Excludes associated-property filters, which also have an
 * `association` key but additionally carry `target_property`.
 */
export function isAssociationFilter(
  filter: CrmFilter
): filter is AssociationFilter {
  return 'association' in filter && !('target_property' in filter);
}

/**
 * Type guard to check if a filter is an AnyOfFilter (nested OR-block).
 */
export function isAnyOfFilter(filter: CrmFilter): filter is AnyOfFilter {
  return 'any_of' in filter;
}

/**
 * Type guard to check if a filter is an AllOfFilter (nested AND-block).
 */
export function isAllOfFilter(filter: CrmFilter): filter is AllOfFilter {
  return 'all_of' in filter;
}

/**
 * Group of filters combined with AND logic.
 * Supports both property filters and association filters.
 */
export interface FilterGroup {
  filters: CrmFilter[];
}

// ====================
// Group By Types
// ====================

/**
 * Legacy string-based group by (property name only).
 */
export type PropertyGroupBy = string;

/**
 * Explicit property group by with property key.
 */
export interface PropertyGroupByObject {
  property: string;
}

/**
 * Association group by for grouping by associated objects.
 */
export interface AssociationGroupBy {
  association: string; // Association type name
}

/**
 * Union type for group by items - supports legacy string format and explicit objects.
 * Backward compatible: accepts both string (legacy) and object formats.
 */
export type GroupByItem =
  | PropertyGroupBy
  | PropertyGroupByObject
  | AssociationGroupBy;

/**
 * Type guard to check if a group by item is a string (legacy format).
 */
export function isPropertyGroupByString(
  item: GroupByItem
): item is PropertyGroupBy {
  return typeof item === 'string';
}

/**
 * Type guard to check if a group by item is a PropertyGroupByObject.
 */
export function isPropertyGroupByObject(
  item: GroupByItem
): item is PropertyGroupByObject {
  return typeof item === 'object' && item !== null && 'property' in item;
}

/**
 * Type guard to check if a group by item is an AssociationGroupBy.
 */
export function isAssociationGroupBy(
  item: GroupByItem
): item is AssociationGroupBy {
  return typeof item === 'object' && item !== null && 'association' in item;
}

/**
 * Filters for querying CRM objects.
 */
export interface CrmObjectFilters {
  properties?: string[]; // Property names to return in response (required for POST /query endpoint)
  filter_groups?: FilterGroup[]; // OR filter groups (POST /query endpoint)
  pipeline_name?: string; // Filter deals by pipeline
  stage_name?: string; // Filter deals by stage
  search?: string; // Search across properties (case-insensitive label search)
  search_properties?: string[]; // Property names to include in search
  associated_with_entity_id?: string; // Filter by associated entity (backend filtering)
  entity_id?: string | string[]; // Filter by entity ID(s) for batch fetching
  include_deleted?: boolean; // Include deleted objects in results
  include_total_count?: boolean; // Skip COUNT(*) OVER() window function when false
  include_associated_objects?: boolean; // Include associated objects in response for lookup
  sort?: string; // Property name to sort by
  order?: 'asc' | 'desc'; // Sort order
  limit?: number;
  offset?: number;
  page?: number; // Page number (1-indexed) for PageNumberPagination
  page_size?: number; // Items per page for PageNumberPagination
  [key: string]:
    | string
    | number
    | boolean
    | string[]
    | number[]
    | FilterGroup[]
    | undefined;
}

/**
 * Response from CRM object list query (DRF PageNumberPagination format).
 */
export interface CrmObjectsListResponse {
  count: number | null;
  next: string | null;
  previous: string | null;
  results: CrmObject[];
  associated_objects?: Record<string, CrmObject>; // Lookup for associated objects when include_associated_objects=true
  // Association type names the server actually filtered `results[].associations`
  // to, or null/absent when the request wasn't projected. Diagnostic only —
  // optional so a frontend running against an older backend still type-checks.
  associations_projection?: string[] | null;
}

// ====================
// Contact Helper Types (for convenience)
// ====================

/**
 * Structured phone number format matching backend schema.
 * Backend: PropertyValuePhoneNumber model
 */
export type PhoneNumber = {
  country_code: string; // ISO 3166-1 alpha-2 (e.g., "US", "GB", "IE")
  national_number: string; // Digits only, no country code prefix
};

/**
 * Data for creating a contact.
 */
export interface ContactCreateData {
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: PhoneNumber | null;
  company_entity_id?: string; // UUID of company to associate
}

/**
 * Data for updating a contact.
 */
export interface ContactUpdateData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: PhoneNumber | null;
}

/**
 * Helper to extract contact properties from CrmObject.
 */
export interface ContactProperties {
  ahoy_first_name?: string;
  ahoy_last_name?: string;
  ahoy_email?: string;
  ahoy_phone_number?: PhoneNumber;
  ahoy_timezone?: string;
}

// ====================
// Company Helper Types (for convenience)
// ====================

/**
 * Data for creating a company.
 */
export interface CompanyCreateData {
  name: string;
  domain?: string;
}

/**
 * Data for updating a company.
 */
export interface CompanyUpdateData {
  name?: string;
  domain?: string;
}

/**
 * Helper to extract company properties from CrmObject.
 */
export interface CompanyProperties {
  ahoy_name?: string;
  ahoy_domain?: string;
}

// ====================
// Deal Helper Types (for convenience)
// ====================

/**
 * Data for creating a deal.
 */
export interface DealCreateData {
  name: string;
  amount?: number;
  close_date?: string;
  contact_entity_ids?: string[]; // UUIDs of contacts to associate
  company_entity_id?: string; // UUID of company to associate
}

/**
 * Data for updating a deal.
 */
export interface DealUpdateData {
  name?: string;
  amount?: number;
  close_date?: string;
}

/**
 * Quantity value type for currency amounts, weights, etc.
 * The amount field contains the numeric value (matches PropertyValueQuantity.value).
 */
export interface DealQuantityValue {
  amount: string | number | null;
  unit: string | null;
}

/**
 * Helper to extract deal properties from CrmObject.
 */
export interface DealProperties {
  ahoy_name?: string;
  ahoy_amount?: DealQuantityValue;
  ahoy_close_date?: string;
}

// ====================
// Helper Functions
// ====================

/**
 * Get total count of associations.
 */
export function getAssociationsCount(obj: CrmObject): number {
  return obj.associations?.length || 0;
}

/**
 * Find an association by type.
 */
export function findAssociation(
  obj: CrmObject,
  type: string
): AssociationDetail | undefined {
  return obj.associations?.find(a => a.association_type_name === type);
}

// ====================
// Label Types (unchanged - still using old CRM app)
// ====================

export type LabelType = 'all' | 'contact' | 'company' | 'deal';

export interface Label {
  id: number;
  team: number;
  name: string;
  color: string;
  label_type: LabelType;
  description: string;
  tasks_count: number;
  created_at: string;
  updated_at: string;
}

export interface LabelCreateUpdate {
  name: string;
  color?: string;
  label_type?: LabelType;
  description?: string;
}

export interface LabelsListResponse {
  labels: Label[];
  total: number;
}

export interface LabelFilters {
  search?: string;
  label_type?: LabelType;
  [key: string]: string | number | boolean | undefined;
}

export const LABEL_TYPES: { value: LabelType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'contact', label: 'Contact' },
  { value: 'company', label: 'Company' },
  { value: 'deal', label: 'Deal' },
];

// ====================
// Property Value Helpers
// ====================

/**
 * THE canonical ARRAY ELEMENT-WISE unwrap for the single-level {value} PropertyValue convention.
 * Shape: `{ value: X, ...metadata }` → X, applied per-element for arrays.
 *
 * Three surfaces share this convention — always unwrap through this function:
 *   - CRM object properties:   obj.properties[key]
 *   - timeline event payloads: event.payload.properties[key]
 *   - proposal payloads:       action.payload.payload.properties[key]
 *
 * Behaviour:
 *   - Strips exactly ONE level: `{ value: X, ...metadata }` → X.
 *     X is returned as-is even when X itself has a `value` key.
 *     (nested-value hazard: one strip only — never double-strips)
 *   - For arrays (multi_quantity): maps each element one level.
 *     The same single-strip rule applies per element.
 *   - Any object that has a `value` key is treated as a wrapper;
 *     other sibling fields are metadata and are discarded.
 *   - Raw values (strings, numbers, objects without a `value` key)
 *     are returned unchanged for backward compatibility.
 *   - null / undefined → undefined.
 *
 * For a SINGLE-LEVEL, ARRAY-PRESERVING unwrap (arrays pass through untouched
 * instead of being mapped), use `unwrapPropertyValue` below instead.
 */
export function getPropertyValue<T>(
  propValue: PropertyValue | PropertyValue[] | T | undefined
): T | undefined {
  if (propValue === undefined || propValue === null) {
    return undefined;
  }

  // Handle arrays (multi_quantity): extract value from each element
  if (Array.isArray(propValue)) {
    return propValue.map(item => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return (item as PropertyValue).value;
      }
      return item;
    }) as T;
  }

  // Check if it's a PropertyValue wrapper (has 'value' key and is an object)
  // Note: We only check for 'value' since metadata fields are optional
  if (
    typeof propValue === 'object' &&
    propValue !== null &&
    'value' in propValue
  ) {
    return (propValue as PropertyValue).value as T;
  }
  // Return as-is for raw values (backward compatibility)
  return propValue as T;
}

/**
 * Single-level, array-preserving unwrap of a serialized CRM property value.
 *
 * The wire wraps every scalar/object property value once as `{ value: <actual>, ...metadata }`.
 * This is the SAME serialized convention across all three property-bearing surfaces:
 *   - CRM object reads (`obj.properties[name]`)
 *   - timeline event payloads (created/updated item + event renderers)
 *   - proposal payloads (action table / accordion)
 * so a single decoder is correct for all of them.
 *
 * Behavior: unwrap ONE `{ value }` wrapper on a non-array object; pass arrays
 * (multi-value properties) and raw primitives straight through untouched. Callers
 * that need per-element unwrap of a multi-value array use `getPropertyValue`.
 */
export function unwrapPropertyValue(value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'value' in value
  ) {
    return (value as { value: unknown }).value;
  }
  return value;
}

// ====================
// Utility Functions
// ====================

/**
 * Helper to get a contact's full name from properties.
 */
export function getContactFullName(obj: CrmObject): string {
  const firstName =
    getPropertyValue<string>(obj.properties?.ahoy_first_name) || '';
  const lastName =
    getPropertyValue<string>(obj.properties?.ahoy_last_name) || '';
  return `${firstName} ${lastName}`.trim() || 'Unnamed Contact';
}

/**
 * Helper to get a company's name from properties.
 */
export function getCompanyName(obj: CrmObject): string {
  const name = getPropertyValue<string>(obj.properties?.ahoy_name);
  return name || 'Unnamed Company';
}

/**
 * Helper to get a deal's name from properties.
 */
export function getDealName(obj: CrmObject): string {
  const name = getPropertyValue<string>(obj.properties?.ahoy_name);
  return name || 'Unnamed Deal';
}

/**
 * Helper to get a deal's amount from properties.
 * Returns the numeric amount from the quantity value, or null if not available.
 */
export function getDealAmount(obj: CrmObject): number | null {
  const quantityValue = getPropertyValue<DealQuantityValue>(
    obj.properties?.ahoy_amount
  );
  if (
    !quantityValue ||
    quantityValue.amount === null ||
    quantityValue.amount === undefined
  ) {
    return null;
  }
  const numAmount =
    typeof quantityValue.amount === 'string'
      ? parseFloat(quantityValue.amount)
      : quantityValue.amount;
  return isNaN(numAmount) ? null : numAmount;
}

export interface BulkDeleteSyncSuccessResponse {
  success: true;
  mode: 'sync';
  deleted_count: number;
  has_more: boolean;
}

export interface BulkDeleteAsyncSuccessResponse {
  success: true;
  mode: 'async';
  job_id: number;
  total_count: number;
  status_url: string;
}

export type BulkDeleteSuccessResponse =
  | BulkDeleteSyncSuccessResponse
  | BulkDeleteAsyncSuccessResponse;

export interface BulkDeleteErrorResponse {
  success: false;
  error: string;
  error_type: string;
}

export type BulkDeleteResponse =
  | BulkDeleteSuccessResponse
  | BulkDeleteErrorResponse;

export type BulkChangeStageSuccessResponse =
  | {
      success: true;
      mode: 'sync';
      changed_count: number;
      skipped_total: number;
      has_more: boolean;
    }
  | {
      success: true;
      mode: 'async';
      job_id: number;
      total_count: number;
      status_url: string;
    };

export type BulkEnrichSuccessResponse =
  | {
      success: true;
      mode: 'sync';
      enriched_count: number;
      skipped_ineligible: number;
      has_more: boolean;
    }
  | {
      success: true;
      mode: 'async';
      job_id: number;
      total_count: number;
      status_url: string;
    };

export type BulkUpdateSuccessResponse =
  | {
      success: true;
      mode: 'sync';
      updated_count?: number;
      changed_count?: number;
      has_more: boolean;
    }
  | {
      success: true;
      mode: 'async';
      job_id: number;
      total_count: number;
      status_url: string;
    };

export type BulkActionJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled';

export interface BulkActionJobProgress {
  processed: number;
  total: number;
}

export interface BulkActionJobResultSummary {
  deleted_count?: number;
  changed_count?: number;
  enriched_count?: number;
  failed_count: number;
  error_categories: Record<string, number>;
}

export interface BulkActionJob {
  id: number;
  action_type: 'delete' | 'change_stage' | 'enrich';
  entity_type_name: string;
  status: BulkActionJobStatus;
  progress: BulkActionJobProgress | null;
  result_summary: BulkActionJobResultSummary | null;
  error_message: string | null;
  last_heartbeat_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export const TERMINAL_BULK_ACTION_JOB_STATUSES: ReadonlySet<BulkActionJobStatus> =
  new Set<BulkActionJobStatus>([
    'completed',
    'completed_with_errors',
    'failed',
    'cancelled',
  ]);
